import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { DisplaySchedule } from "@/types/display";
import { HolidayValue } from "@/constants/holidays";

type DisplayHolidayYear = Database['public']['Tables']['display_holiday_years']['Row'];
type DisplayHolidayYearInsert = Database['public']['Tables']['display_holiday_years']['Insert'];
type DisplayHolidayYearUpdate = Database['public']['Tables']['display_holiday_years']['Update'];

export interface HolidayContent {
  description?: string;
  photos?: string[];
  videos?: string[];
  schedule?: DisplaySchedule;
}

/**
 * Safely upsert a display holiday year record
 * Handles the unique constraint on (display_id, holiday, year)
 *
 * @param displayId - The display ID
 * @param holiday - The holiday name (from standardized list)
 * @param year - The year (defaults to current year)
 * @param content - Optional content for the holiday year
 * @returns The display holiday year record or throws error
 */
export const upsertDisplayHolidayYear = async (
  displayId: number,
  holiday: HolidayValue,
  year?: number,
  content?: HolidayContent
): Promise<DisplayHolidayYear> => {
  const targetYear = year || new Date().getFullYear();

  try {
    // First, try to find existing record
    const { data: existingRecord, error: fetchError } = await supabase
      .from('display_holiday_years')
      .select('*')
      .eq('display_id', displayId)
      .eq('holiday', holiday)
      .eq('year', targetYear)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching display holiday year:', fetchError);
      throw fetchError;
    }

    if (existingRecord) {
      // Update existing record if content is provided
      if (content) {
        const updateData: DisplayHolidayYearUpdate = {
          description: content.description,
          photos: content.photos as any,
          videos: content.videos as any,
          schedule: content.schedule as any,
          updated_at: new Date().toISOString()
        };

        const { data: updatedRecord, error: updateError } = await supabase
          .from('display_holiday_years')
          .update(updateData)
          .eq('id', existingRecord.id)
          .select()
          .single();

        if (updateError) {
          console.error('Error updating display holiday year:', updateError);
          throw updateError;
        }

        return updatedRecord!;
      }
      return existingRecord;
    } else {
      // Create new record - need to get current user ID for RLS
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        console.error('Error getting current user:', userError);
        throw new Error('User must be authenticated to create holiday year records');
      }

      const insertData: DisplayHolidayYearInsert = {
        display_id: displayId,
        holiday,
        year: targetYear,
        user_id: user.id, // Required for RLS policy
        description: content?.description || null,
        photos: (content?.photos as any) || null,
        videos: (content?.videos as any) || null,
        schedule: (content?.schedule as any) || null
      };

      const { data: newRecord, error: insertError } = await supabase
        .from('display_holiday_years')
        .insert(insertData)
        .select()
        .single();

      if (insertError) {
        console.error('Error creating display holiday year:', insertError);
        throw insertError;
      }

      return newRecord!;
    }
  } catch (error) {
    console.error('Error in upsertDisplayHolidayYear:', error);
    throw error;
  }
};

/**
 * Load all holiday year records for a display
 * Returns a map of holiday name to holiday year data
 *
 * @param displayId - The display ID
 * @param year - Optional year filter (defaults to current year)
 * @returns Map of holiday name to holiday year record
 */
export const loadDisplayHolidayYears = async (
  displayId: number,
  year?: number
): Promise<Record<string, DisplayHolidayYear>> => {
  const targetYear = year || new Date().getFullYear();

  try {
    let query = supabase
      .from('display_holiday_years')
      .select('*')
      .eq('display_id', displayId);

    if (year !== undefined) {
      query = query.eq('year', targetYear);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error loading display holiday years:', error);
      throw error;
    }

    // Convert array to map keyed by holiday
    const holidayMap: Record<string, DisplayHolidayYear> = {};
    data?.forEach(record => {
      holidayMap[record.holiday] = record;
    });

    return holidayMap;
  } catch (error) {
    console.error('Error in loadDisplayHolidayYears:', error);
    throw error;
  }
};

/**
 * Load a specific holiday year record
 *
 * @param displayId - The display ID
 * @param holiday - The holiday name
 * @param year - The year (defaults to current year)
 * @returns The holiday year record or null if not found
 */
export const loadDisplayHolidayYear = async (
  displayId: number,
  holiday: HolidayValue,
  year?: number
): Promise<DisplayHolidayYear | null> => {
  const targetYear = year || new Date().getFullYear();

  try {
    const { data, error } = await supabase
      .from('display_holiday_years')
      .select('*')
      .eq('display_id', displayId)
      .eq('holiday', holiday)
      .eq('year', targetYear)
      .maybeSingle();

    if (error) {
      console.error('Error loading display holiday year:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in loadDisplayHolidayYear:', error);
    throw error;
  }
};

/**
 * Save holiday-specific content (photos, videos, schedule, description)
 *
 * @param displayHolidayYearId - The display holiday year ID
 * @param content - The content to save
 * @returns The updated record
 */
export const saveHolidayContent = async (
  displayHolidayYearId: string,
  content: HolidayContent
): Promise<DisplayHolidayYear> => {
  try {
    const updateData: DisplayHolidayYearUpdate = {
      description: content.description,
      photos: content.photos as any,
      videos: content.videos as any,
      schedule: content.schedule as any,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('display_holiday_years')
      .update(updateData)
      .eq('id', displayHolidayYearId)
      .select()
      .single();

    if (error) {
      console.error('Error saving holiday content:', error);
      throw error;
    }

    return data!;
  } catch (error) {
    console.error('Error in saveHolidayContent:', error);
    throw error;
  }
};

/**
 * Delete a display holiday year record
 *
 * @param displayHolidayYearId - The display holiday year ID to delete
 */
export const deleteDisplayHolidayYear = async (
  displayHolidayYearId: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('display_holiday_years')
      .delete()
      .eq('id', displayHolidayYearId);

    if (error) {
      console.error('Error deleting display holiday year:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteDisplayHolidayYear:', error);
    throw error;
  }
};

/**
 * Get the display's active holidays for determining which tabs to show
 *
 * @param displayId - The display ID
 * @returns Array of holiday names this display celebrates
 */
export const getDisplayHolidays = async (
  displayId: number
): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('displays')
      .select('holidays')
      .eq('id', displayId)
      .single();

    if (error) {
      console.error('Error fetching display holidays:', error);
      throw error;
    }

    return data?.holidays || [];
  } catch (error) {
    console.error('Error in getDisplayHolidays:', error);
    throw error;
  }
};
